
import { ArrowRight, Award, Share2, Users } from "lucide-react";
import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

interface ReferralSectionProps {
  isReferral?: boolean;
  referralCode?: string;
}

const ReferralSection = ({ isReferral }: ReferralSectionProps) => {
  return (
    <section className="py-16 bg-gradient-to-b from-white to-neutral-100">
      <div className="container-padding">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <span className="bg-accent-purple/10 text-accent-purple px-4 py-1.5 rounded-full text-sm font-medium inline-block mb-4">
            REFER & EARN
          </span>
          <motion.h2 
            className="text-3xl font-bold mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Share The Mat Monster, Earn Rewards
          </motion.h2>
          <p className="text-neutral-600">
            {isReferral ? (
              <>
                You've been invited to join The Mat Monster community! 
                <Badge variant="secondary" className="ml-2">30% OFF</Badge>
              </>
            ) : (
              "Invite friends to join The Mat Monster community and earn premium membership rewards, exclusive merchandise, and more for every successful referral."
            )}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16">
          <Card className="p-6 bg-white shadow-md rounded-xl border-0 transition-all hover:shadow-lg">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-accent-purple/10 rounded-full flex items-center justify-center mb-4">
                <Share2 className="w-6 h-6 text-accent-purple" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Share Your Link</h3>
              <p className="text-neutral-600 mb-4">Get your unique referral link and share it with friends through email, social media, or direct message.</p>
            </div>
          </Card>
          
          <Card className="p-6 bg-white shadow-md rounded-xl border-0 transition-all hover:shadow-lg">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-accent-purple/10 rounded-full flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-accent-purple" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Friends Join</h3>
              <p className="text-neutral-600 mb-4">When your friends sign up using your link, they'll be connected to your account as referrals.</p>
            </div>
          </Card>
          
          <Card className="p-6 bg-white shadow-md rounded-xl border-0 transition-all hover:shadow-lg">
            <div className="flex flex-col items-center text-center">
              <div className="w-12 h-12 bg-accent-purple/10 rounded-full flex items-center justify-center mb-4">
                <Award className="w-6 h-6 text-accent-purple" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Earn Rewards</h3>
              <p className="text-neutral-600 mb-4">For each successful referral, you'll earn points toward premium features, membership discounts, and exclusive gear.</p>
            </div>
          </Card>
        </div>

        <div className="bg-white rounded-xl p-8 shadow-md max-w-3xl mx-auto border border-neutral-200">
          <div className="flex flex-col md:flex-row gap-8 items-center">
            <div className="flex-1">
              <h3 className="text-2xl font-bold mb-4">
                {isReferral ? "Ready to claim your 30% discount?" : "Ready to start earning?"}
              </h3>
              <p className="text-neutral-600 mb-6">
                {isReferral 
                  ? "Sign up now to get 30% off your first 3 months of premium membership." 
                  : "Sign up today and get your personal referral link to share with friends and training partners."}
              </p>
              <button className="button-primary flex items-center gap-2">
                Create Your Account <ArrowRight className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 flex justify-center">
              <div className="relative">
                <div className="w-48 h-48 bg-accent-purple/5 rounded-full flex items-center justify-center">
                  <div className="w-32 h-32 bg-accent-purple/10 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-accent-purple">30% Off</span>
                  </div>
                </div>
                <div className="absolute top-0 right-0 bg-accent-purple text-white rounded-full px-3 py-1 text-sm font-medium">
                  For Friends
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReferralSection;
